import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

const SEARCH_MIN_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MAX_RESULTS = 5;

/**
 * A floating chat assistant that helps customers find products, answer
 * frequent questions, and reach WhatsApp. Opening/closing is handled natively
 * by the Popover API (see the `popover` attribute in the markup); this
 * component handles quick-reply clicks and simple keyword matching on the
 * free-text question field. No AI/external API involved — matching is a
 * plain keyword lookup against the intents configured in Theme settings.
 *
 * @typedef {Object} ChatbotIntent
 * @property {string} id
 * @property {'link'|'message'} type
 * @property {string} [url]
 * @property {string} [text]
 * @property {string[]} keywords
 *
 * @typedef {Object} ChatbotWidgetRefs
 * @property {HTMLElement} messages
 * @property {HTMLFormElement} questionForm
 * @property {HTMLInputElement} questionInput
 * @property {HTMLScriptElement} intentsData
 * @property {HTMLElement} quickReplies
 * @property {HTMLElement} searchResults
 * @property {HTMLTemplateElement} moneyFormat
 *
 * @extends {Component<ChatbotWidgetRefs>}
 */
export class ChatbotWidget extends Component {
  requiredRefs = ['messages'];

  /** @type {ChatbotIntent[] | null} */
  #intents = null;

  /** @type {AbortController | null} */
  #searchAbortController = null;

  /** @type {string | null} Query currently in flight, used to avoid duplicate concurrent requests. */
  #pendingQuery = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intents = this.#parseIntents();
  }

  /** @returns {ChatbotIntent[]} */
  #parseIntents() {
    const script = this.refs.intentsData;
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent ?? '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('chatbot-widget: no se pudo interpretar la configuración de intenciones', error);
      return [];
    }
  }

  /**
   * @param {{action: string}} data
   * @param {Event} event
   */
  handleQuickReply(data, event) {
    const button = /** @type {HTMLButtonElement | null} */ (
      event.target instanceof Element ? event.target.closest('button') : null
    );

    switch (data.action) {
      case 'pokemon':
        window.location.href = this.dataset.collectionPokemon ?? '/collections';
        break;
      case 'onepiece':
        window.location.href = this.dataset.collectionOnepiece ?? '/collections';
        break;
      case 'accessories':
        window.location.href = this.dataset.collectionAccessories ?? '/collections';
        break;
      case 'whatsapp':
        window.open(this.dataset.whatsappUrl, '_blank', 'noopener');
        break;
      case 'shipping':
        this.#addBotMessage(this.dataset.shippingInfo ?? '');
        if (button) button.disabled = true;
        break;
      default:
        break;
    }
  }

  /**
   * Handles the free-text question form: matches the question against the
   * configured intents by keyword, and responds accordingly.
   *
   * @param {Event} event
   */
  handleUserQuestion(event) {
    event.preventDefault();
    const { questionInput } = this.refs;
    if (!questionInput) return;

    const question = questionInput.value.trim();
    if (!question) return;

    this.#addUserMessage(question);
    questionInput.value = '';
    this.#searchDebounced.cancel();
    this.#showQuickReplies();

    const intent = this.#matchIntent(question);

    if (!intent) {
      this.#addFallbackMessage();
      return;
    }

    if (intent.type === 'link' && intent.url) {
      window.location.href = intent.url;
      return;
    }

    if (intent.type === 'message' && intent.text) {
      this.#addBotMessage(intent.text);
    }
  }

  /**
   * Live product search as the customer types (debounced). Triggered on every
   * `input` event; the actual fetch is debounced by SEARCH_DEBOUNCE_MS and
   * only runs once the query reaches SEARCH_MIN_LENGTH characters.
   */
  handleQuestionInput() {
    const value = this.refs.questionInput.value.trim();

    if (value.length < SEARCH_MIN_LENGTH) {
      this.#searchDebounced.cancel();
      this.#abortSearch();
      this.#showQuickReplies();
      return;
    }

    this.#searchDebounced(value);
  }

  /** @type {(value: string) => void} */
  #searchDebounced = debounce((value) => this.#runProductSearch(value), SEARCH_DEBOUNCE_MS);

  /**
   * Fetches product matches from Shopify's native predictive search endpoint
   * (same-origin, no Storefront API token needed). Cancels any previous
   * in-flight request and skips issuing a duplicate request for the same text.
   *
   * @param {string} query
   */
  async #runProductSearch(query) {
    if (this.#pendingQuery === query) return;
    this.#pendingQuery = query;
    this.#abortSearch();

    const { searchResults, quickReplies } = this.refs;
    quickReplies.hidden = true;
    searchResults.hidden = false;
    searchResults.replaceChildren(this.#buildStatusMessage('Buscando...'));

    const controller = new AbortController();
    this.#searchAbortController = controller;

    // Field priority: title, vendor, product_type and SKU are exact/structured product data.
    // `tag` is deliberately excluded — this catalog uses broad cross-sell tags (e.g. every
    // sleeve product is tagged "one piece") that make tag matches noisy and imprecise.
    // Collections are matched as a separate resource type below, since Shopify's predictive
    // search has no "collection" field for product matching.
    const searchUrl = new URL(Theme.routes.predictive_search_url, location.origin);
    searchUrl.searchParams.set('q', query);
    searchUrl.searchParams.set('resources[type]', 'product,collection');
    searchUrl.searchParams.set('resources[limit]', String(SEARCH_MAX_RESULTS));
    searchUrl.searchParams.set('resources[options][unavailable_products]', 'show');
    searchUrl.searchParams.set('resources[options][fields]', 'title,vendor,product_type,variants.sku');

    try {
      const response = await fetch(searchUrl, { signal: controller.signal });
      if (controller.signal.aborted) return;

      const data = await response.json();
      if (controller.signal.aborted) return;

      // Discard stale responses if the customer kept typing after this request was sent.
      if (this.refs.questionInput.value.trim() !== query) return;

      const results = data?.resources?.results ?? {};
      const collections = /** @type {Array<Record<string, any>>} */ (results.collections ?? []);
      const products = /** @type {Array<Record<string, any>>} */ (results.products ?? []);
      const productSlots = Math.max(SEARCH_MAX_RESULTS - Math.min(collections.length, 2), 1);

      this.#renderSearchResults(collections.slice(0, 2), products.slice(0, productSlots));
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error('chatbot-widget: fallo al buscar productos', error);
      if (this.refs.questionInput.value.trim() === query) {
        this.refs.searchResults.replaceChildren(
          this.#buildStatusMessage('No se pudo completar la búsqueda. Inténtalo de nuevo.')
        );
      }
    } finally {
      if (this.#pendingQuery === query) this.#pendingQuery = null;
    }
  }

  #abortSearch() {
    this.#searchAbortController?.abort();
    this.#searchAbortController = null;
  }

  #showQuickReplies() {
    this.#abortSearch();
    this.#pendingQuery = null;
    this.refs.quickReplies.hidden = false;
    this.refs.searchResults.hidden = true;
    this.refs.searchResults.replaceChildren();
  }

  /**
   * @param {Array<Record<string, any>>} collections
   * @param {Array<Record<string, any>>} products
   */
  #renderSearchResults(collections, products) {
    const { searchResults } = this.refs;
    searchResults.replaceChildren();

    if (collections.length === 0 && products.length === 0) {
      searchResults.append(this.#buildNoResultsBlock());
      return;
    }

    for (const collection of collections) {
      searchResults.append(this.#buildCollectionSuggestion(collection));
    }

    for (const product of products) {
      searchResults.append(this.#buildProductCard(product));
    }
  }

  /** @param {Record<string, any>} collection */
  #buildCollectionSuggestion(collection) {
    const link = document.createElement('a');
    link.className = 'chatbot-widget__quick-reply';
    link.href = collection.url ?? '#';
    link.textContent = `Ver colección: ${collection.title ?? ''}`;
    return link;
  }

  /** @param {Record<string, any>} product */
  #buildProductCard(product) {
    const card = document.createElement('div');
    card.className = 'chatbot-widget__product-card';

    const imageUrl = product.image ?? product.featured_image?.url;
    if (imageUrl) {
      const img = document.createElement('img');
      img.className = 'chatbot-widget__product-image';
      img.src = imageUrl;
      img.width = 48;
      img.height = 48;
      img.loading = 'lazy';
      img.alt = '';
      card.append(img);
    }

    const info = document.createElement('div');
    info.className = 'chatbot-widget__product-info';

    const title = document.createElement('p');
    title.className = 'chatbot-widget__product-title';
    title.textContent = product.title ?? '';
    info.append(title);

    const meta = document.createElement('p');
    meta.className = 'chatbot-widget__product-meta';

    const price = document.createElement('span');
    price.className = 'chatbot-widget__product-price';
    price.textContent = this.#formatPrice(product.price);
    meta.append(price);

    const badge = document.createElement('span');
    const available = Boolean(product.available);
    badge.className = `chatbot-widget__product-badge ${
      available ? 'chatbot-widget__product-badge--available' : 'chatbot-widget__product-badge--sold-out'
    }`;
    badge.textContent = available ? 'Disponible' : 'Agotado';
    meta.append(badge);

    info.append(meta);

    const link = document.createElement('a');
    link.className = 'chatbot-widget__product-link';
    link.href = product.url ?? '#';
    link.textContent = 'Ver producto';
    info.append(link);

    card.append(info);
    return card;
  }

  /** @param {string | number | undefined} priceValue */
  #formatPrice(priceValue) {
    const currency = this.dataset.currency || '';
    if (priceValue == null || !currency) return priceValue != null ? String(priceValue) : '';

    const minorUnits = convertMoneyToMinorUnits(String(priceValue), currency);
    if (minorUnits === null) return String(priceValue);

    const format = this.refs.moneyFormat?.content?.textContent?.trim() || '{{amount}}';
    return formatMoney(minorUnits, format, currency);
  }

  #buildNoResultsBlock() {
    const wrapper = document.createElement('div');
    wrapper.className = 'chatbot-widget__no-results';

    const text = document.createElement('p');
    text.className = 'chatbot-widget__no-results-text';
    text.textContent = 'No he encontrado productos relacionados.';
    wrapper.append(text);

    const actions = document.createElement('div');
    actions.className = 'chatbot-widget__no-results-actions';

    const pokemonLink = document.createElement('a');
    pokemonLink.className = 'chatbot-widget__quick-reply';
    pokemonLink.href = this.dataset.collectionPokemon ?? '#';
    pokemonLink.textContent = 'Ver colección Pokémon';

    const onePieceLink = document.createElement('a');
    onePieceLink.className = 'chatbot-widget__quick-reply';
    onePieceLink.href = this.dataset.collectionOnepiece ?? '#';
    onePieceLink.textContent = 'Ver colección One Piece';

    const whatsappLink = document.createElement('a');
    whatsappLink.className = 'chatbot-widget__quick-reply chatbot-widget__quick-reply--whatsapp';
    whatsappLink.href = this.dataset.whatsappUrl ?? '#';
    whatsappLink.target = '_blank';
    whatsappLink.rel = 'noopener';
    whatsappLink.textContent = 'Hablar por WhatsApp';

    actions.append(pokemonLink, onePieceLink, whatsappLink);
    wrapper.append(actions);

    return wrapper;
  }

  /** @param {string} text */
  #buildStatusMessage(text) {
    const status = document.createElement('p');
    status.className = 'chatbot-widget__search-status';
    status.textContent = text;
    return status;
  }

  /**
   * @param {string} question
   * @returns {ChatbotIntent | null}
   */
  #matchIntent(question) {
    const normalized = this.#normalize(question);
    const intents = this.#intents ?? [];

    for (const intent of intents) {
      const keywords = intent.keywords ?? [];
      const hasMatch = keywords.some((keyword) => keyword && normalized.includes(this.#normalize(keyword)));
      if (hasMatch) return intent;
    }

    return null;
  }

  /**
   * Lowercases and strips accents so "Pokémon"/"pokemon"/"POKÉMON" all match.
   *
   * @param {string} text
   */
  #normalize(text) {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  /** @param {string} text */
  #addUserMessage(text) {
    const { messages } = this.refs;
    const message = document.createElement('div');
    message.className = 'chatbot-widget__message chatbot-widget__message--user';
    message.textContent = text;
    messages.append(message);
    messages.scrollTop = messages.scrollHeight;
  }

  /** @param {string} text */
  #addBotMessage(text) {
    if (!text) return;
    const { messages } = this.refs;
    const message = document.createElement('div');
    message.className = 'chatbot-widget__message chatbot-widget__message--bot';
    message.textContent = text;
    messages.append(message);
    messages.scrollTop = messages.scrollHeight;
  }

  #addFallbackMessage() {
    const { messages } = this.refs;
    const message = document.createElement('div');
    message.className = 'chatbot-widget__message chatbot-widget__message--bot';
    message.textContent = this.dataset.fallbackMessage ?? '';

    const whatsappUrl = this.dataset.whatsappUrl;
    if (whatsappUrl) {
      const action = document.createElement('a');
      action.className = 'chatbot-widget__message-action';
      action.href = whatsappUrl;
      action.target = '_blank';
      action.rel = 'noopener';
      action.textContent = 'Hablar por WhatsApp';
      message.append(document.createElement('br'), action);
    }

    messages.append(message);
    messages.scrollTop = messages.scrollHeight;
  }
}

if (!customElements.get('chatbot-widget')) {
  customElements.define('chatbot-widget', ChatbotWidget);
}
